import { useEffect, useState, useRef } from "react";
import {
  fetchKnowledge, saveKnowledge,
  fetchKeywords, addKeyword, deleteKeyword,
} from "../lib/api";
import type { KnowledgeEntry, WatchKeyword } from "../lib/types";
import { cn } from "../lib/cn";

// ─── Domain order + icons ─────────────────────────────────────────────────────

const DOMAIN_META: Record<string, { icon: string; description: string }> = {
  GLOBAL: {
    icon: "🏛️",
    description: "Applies to every bill analysis. Use this for background DECAL context the AI should always know — org structure, key programs, political sensitivities.",
  },
  LICENSING_COMPLIANCE: {
    icon: "📋",
    description: "Licensing rules, inspections, CCLC / FCCLH provider types, DECAL enforcement authority.",
  },
  HEALTH_SAFETY: {
    icon: "🏥",
    description: "Health & safety standards in child care settings — immunizations, nutrition, safe sleep, CPR requirements.",
  },
  PREK_PROGRAMS: {
    icon: "🎓",
    description: "Georgia's Pre-K program, lottery funding formula, GELDS curriculum standards, Head Start partnerships.",
  },
  WORKFORCE: {
    icon: "👩‍🏫",
    description: "Early educator qualifications, background checks, workforce registry, compensation initiatives.",
  },
  QUALITY_INITIATIVES: {
    icon: "⭐",
    description: "Quality Rated (QRIS), NAEYC accreditation, program improvement grants.",
  },
  FAMILY_ACCESS_AFFORDABILITY: {
    icon: "👨‍👩‍👧",
    description: "CAPS subsidy administration, child care tax credits, family waitlists, affordability initiatives.",
  },
  DATA_PRIVACY_REPORTING: {
    icon: "🔒",
    description: "KOALA data system, child/family data privacy, federal reporting requirements.",
  },
  TECHNOLOGY_AI_EDUCATION: {
    icon: "💻",
    description: "EdTech in child care and Pre-K settings, AI tools, screen time policy, digital learning.",
  },
  LOTTERY_HOPE_FUNDING: {
    icon: "🎰",
    description: "Georgia Lottery proceeds, HOPE scholarship formula, sports betting / casino bills that could redirect Pre-K funding.",
  },
  CAMPUS_ADJACENT: {
    icon: "🏫",
    description: "Campus carry, firearms access near child care facilities on college campuses, controlled substances near schools.",
  },
};

const DOMAIN_ORDER = [
  "GLOBAL",
  "LICENSING_COMPLIANCE",
  "HEALTH_SAFETY",
  "PREK_PROGRAMS",
  "WORKFORCE",
  "QUALITY_INITIATIVES",
  "FAMILY_ACCESS_AFFORDABILITY",
  "DATA_PRIVACY_REPORTING",
  "TECHNOLOGY_AI_EDUCATION",
  "LOTTERY_HOPE_FUNDING",
  "CAMPUS_ADJACENT",
];

// ─── Save status indicator ────────────────────────────────────────────────────

type SaveState = "idle" | "saving" | "saved" | "error";

function SaveIndicator({ state }: { state: SaveState }) {
  if (state === "idle") return null;
  return (
    <span className={cn(
      "inline-flex items-center gap-1 text-xs font-medium transition-opacity",
      state === "saving" ? "text-ink-tertiary" :
      state === "saved"  ? "text-teal-600" :
                           "text-risk-high"
    )}>
      {state === "saving" && (
        <svg className="h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
        </svg>
      )}
      {state === "saved" && (
        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
        </svg>
      )}
      {state === "saving" ? "Saving…" : state === "saved" ? "Saved" : "Error saving"}
    </span>
  );
}

// ─── Domain context card ──────────────────────────────────────────────────────

function DomainCard({ entry, onSave }: {
  entry: KnowledgeEntry;
  onSave: (domain: string, label: string, context: string) => Promise<void>;
}) {
  const [text, setText] = useState(entry.context);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const meta = DOMAIN_META[entry.domain];
  const isDirty = text !== entry.context;

  // Auto-save 1.5s after the user stops typing
  function handleChange(val: string) {
    setText(val);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setSaveState("saving");
      try {
        await onSave(entry.domain, entry.label, val);
        setSaveState("saved");
        setTimeout(() => setSaveState("idle"), 2000);
      } catch {
        setSaveState("error");
      }
    }, 1500);
  }

  return (
    <div className={cn(
      "group rounded-2xl border bg-surface-raised transition-shadow duration-200",
      "hover:shadow-sm",
      entry.domain === "GLOBAL"
        ? "border-primary/20 bg-primary-light/30"
        : "border-border-subtle"
    )}>
      <div className="px-5 pt-5 pb-4">
        {/* Header */}
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <span className="text-xl leading-none" aria-hidden>{meta?.icon ?? "📌"}</span>
            <div>
              <h3 className="text-sm font-semibold text-ink">
                {entry.label}
                {entry.domain === "GLOBAL" && (
                  <span className="ml-2 rounded-full bg-primary px-2 py-0.5 text-2xs font-semibold text-white">
                    Global
                  </span>
                )}
              </h3>
              <p className="mt-0.5 text-xs leading-relaxed text-ink-tertiary">
                {meta?.description}
              </p>
            </div>
          </div>
          <SaveIndicator state={saveState} />
        </div>

        {/* Textarea */}
        <textarea
          value={text}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={
            entry.domain === "GLOBAL"
              ? "Add context that applies to every bill analysis — e.g. DECAL's key programs, political sensitivities, fiscal year constraints…"
              : `Add context for ${entry.label} — e.g. specific facilities, known risks, past legislative history DECAL has dealt with…`
          }
          rows={text.length > 120 ? 5 : 3}
          className={cn(
            "w-full resize-none rounded-xl border bg-surface px-3.5 py-3 text-sm leading-relaxed text-ink",
            "placeholder:text-ink-disabled",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-light",
            "transition-colors duration-150",
            isDirty ? "border-primary/40" : "border-border-subtle"
          )}
        />

        {/* Last updated */}
        {entry.updated_at && entry.context && (
          <p className="mt-1.5 text-right text-2xs text-ink-disabled">
            Last updated {new Date(entry.updated_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Watch keyword row ────────────────────────────────────────────────────────

function KeywordRow({ kw, onDelete }: { kw: WatchKeyword; onDelete: (id: number) => void }) {
  const [confirming, setConfirming] = useState(false);

  return (
    <div className="group flex items-center gap-3 rounded-xl border border-border-subtle bg-surface px-4 py-3 transition-colors hover:border-border">
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-light">
        <svg className="h-3.5 w-3.5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 111 11a6 6 0 0116 0z" />
        </svg>
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-mono text-sm font-medium text-ink">{kw.keyword}</p>
        {kw.note && <p className="mt-0.5 text-xs text-ink-tertiary">{kw.note}</p>}
      </div>
      <span className="shrink-0 text-2xs text-ink-disabled">
        {new Date(kw.added_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
      </span>
      {confirming ? (
        <div className="flex items-center gap-2">
          <span className="text-xs text-ink-secondary">Remove?</span>
          <button onClick={() => onDelete(kw.id)}
            className="rounded-full bg-risk-high px-2.5 py-1 text-2xs font-semibold text-white hover:opacity-80">
            Yes
          </button>
          <button onClick={() => setConfirming(false)}
            className="rounded-full border border-border px-2.5 py-1 text-2xs font-medium text-ink-secondary hover:bg-surface-sunken">
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={() => setConfirming(true)}
          className="shrink-0 rounded-full p-1.5 text-ink-disabled opacity-0 transition-opacity hover:bg-surface-sunken hover:text-ink-secondary group-hover:opacity-100"
          aria-label={`Remove keyword "${kw.keyword}"`}
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

// ─── Add keyword form ─────────────────────────────────────────────────────────

function AddKeywordForm({ onAdd }: { onAdd: (keyword: string, note: string) => Promise<void> }) {
  const [keyword, setKeyword] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!keyword.trim()) return;
    setBusy(true);
    try {
      await onAdd(keyword.trim(), note.trim());
      setKeyword("");
      setNote("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-2xl border border-dashed border-border bg-surface px-5 py-4">
      <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-ink-tertiary">Add watch keyword</p>
      <div className="flex flex-col gap-2.5 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label className="mb-1 block text-xs font-medium text-ink-secondary" htmlFor="kw-input">
            Keyword or phrase
          </label>
          <input
            id="kw-input"
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="e.g. lottery formula"
            className={cn(
              "w-full rounded-xl border border-border-subtle bg-surface-raised px-3.5 py-2 text-sm text-ink",
              "placeholder:text-ink-disabled focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-light"
            )}
          />
        </div>
        <div className="flex-1">
          <label className="mb-1 block text-xs font-medium text-ink-secondary" htmlFor="kw-note">
            Why we watch this (optional)
          </label>
          <input
            id="kw-note"
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Could reduce Pre-K allocation"
            className={cn(
              "w-full rounded-xl border border-border-subtle bg-surface-raised px-3.5 py-2 text-sm text-ink",
              "placeholder:text-ink-disabled focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-light"
            )}
          />
        </div>
        <button
          type="submit"
          disabled={busy || !keyword.trim()}
          className={cn(
            "inline-flex shrink-0 items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white",
            "transition-colors hover:bg-primary-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
            (busy || !keyword.trim()) && "cursor-not-allowed opacity-50"
          )}
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Add
        </button>
      </div>
    </form>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function Skeleton() {
  return (
    <div className="animate-pulse space-y-4">
      {[1,2,3].map((n) => (
        <div key={n} className="rounded-2xl border border-border-subtle bg-surface-raised p-5 space-y-3">
          <div className="flex items-center gap-2.5">
            <div className="h-7 w-7 rounded-full bg-surface-sunken" />
            <div className="h-4 w-36 rounded bg-surface-sunken" />
          </div>
          <div className="h-16 w-full rounded-xl bg-surface-sunken" />
        </div>
      ))}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function KnowledgeBase() {
  const [entries, setEntries]   = useState<KnowledgeEntry[]>([]);
  const [keywords, setKeywords] = useState<WatchKeyword[]>([]);
  const [loading, setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState<"context" | "keywords">("context");

  useEffect(() => {
    Promise.all([fetchKnowledge(), fetchKeywords()])
      .then(([kb, kws]) => {
        // Sort by DOMAIN_ORDER
        const sorted = DOMAIN_ORDER
          .map((d) => kb.find((e) => e.domain === d))
          .filter((e): e is KnowledgeEntry => !!e);
        // Append any domains not in the order list
        kb.forEach((e) => { if (!DOMAIN_ORDER.includes(e.domain)) sorted.push(e); });
        setEntries(sorted);
        setKeywords(kws);
      })
      .finally(() => setLoading(false));
  }, []);

  async function handleSave(domain: string, label: string, context: string) {
    await saveKnowledge(domain, label, context);
    setEntries((prev) =>
      prev.map((e) => e.domain === domain ? { ...e, context, updated_at: new Date().toISOString() } : e)
    );
  }

  async function handleAddKeyword(keyword: string, note: string) {
    const kw = await addKeyword(keyword, note);
    setKeywords((prev) => [kw, ...prev]);
  }

  async function handleDeleteKeyword(id: number) {
    await deleteKeyword(id);
    setKeywords((prev) => prev.filter((k) => k.id !== id));
  }

  const filledCount = entries.filter((e) => e.context.trim()).length;

  return (
    <div className="mx-auto max-w-3xl animate-fade-in px-6 py-8">

      {/* Page header */}
      <div className="mb-8">
        <div className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary-light px-3 py-1">
          <svg className="h-3.5 w-3.5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
          </svg>
          <span className="text-2xs font-semibold text-primary">Institutional Knowledge</span>
        </div>
        <h1 className="text-3xl font-semibold tracking-tight text-ink">Knowledge Base</h1>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-ink-secondary">
          Teach the AI what DECAL knows. Context you add here is injected into every bill analysis —
          so the system understands <em>why</em> a bill matters in DECAL's own language, not just generic legislative text.
        </p>
        {!loading && filledCount > 0 && (
          <p className="mt-3 text-xs text-ink-tertiary">
            <span className="font-semibold text-primary">{filledCount}</span> of {entries.length} domains have context · {keywords.length} watch keywords
          </p>
        )}
      </div>

      {/* Tab switcher */}
      <div className="mb-6 flex gap-px rounded-xl border border-border bg-surface-sunken p-1" role="tablist">
        {(["context", "keywords"] as const).map((tab) => (
          <button
            key={tab}
            role="tab"
            aria-selected={activeTab === tab}
            onClick={() => setActiveTab(tab)}
            className={cn(
              "flex-1 rounded-[9px] px-4 py-2 text-sm font-medium transition-all duration-150",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
              activeTab === tab
                ? "bg-surface-raised text-ink shadow-sm"
                : "text-ink-secondary hover:text-ink"
            )}
          >
            {tab === "context" ? "Domain Context" : "Watch Keywords"}
          </button>
        ))}
      </div>

      {loading && <Skeleton />}

      {/* Domain context tab */}
      {!loading && activeTab === "context" && (
        <div className="space-y-3">
          {entries.map((entry) => (
            <DomainCard key={entry.domain} entry={entry} onSave={handleSave} />
          ))}
          <div className="rounded-2xl border border-border-subtle bg-surface p-5">
            <div className="flex items-start gap-3">
              <svg className="mt-0.5 h-4 w-4 shrink-0 text-ink-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
              </svg>
              <p className="text-xs leading-relaxed text-ink-tertiary">
                Changes are saved automatically. Context takes effect on the <strong className="text-ink-secondary">next data refresh</strong> — existing bills are not re-analyzed until the pipeline runs again.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Watch keywords tab */}
      {!loading && activeTab === "keywords" && (
        <div className="space-y-4">
          <AddKeywordForm onAdd={handleAddKeyword} />

          {keywords.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-surface-sunken">
                <svg className="h-6 w-6 text-ink-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 111 11a6 6 0 0116 0z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-ink-secondary">No watch keywords yet</p>
              <p className="mt-1 max-w-xs text-xs text-ink-tertiary">
                Add phrases like "lottery formula" or "campus carry" — any bill containing these words will be flagged for DECAL review even if it doesn't match a standard domain.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {keywords.map((kw) => (
                <KeywordRow key={kw.id} kw={kw} onDelete={handleDeleteKeyword} />
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
}
